import styled, { css } from 'styled-components';

import {
  ANIM,
  COLORS,
  GRADIENTS,
  FIXED_HEADER,
  MOBILE,
  HEADER_DESKTOP_PX,
  HEADER_MOBILE_PX,
  REDUCED_MOTION,
} from '../../theme/tokens';
import { Text } from '../Text/Text';

/** How long an action opening across the row takes (BackHeaderProps.actionOpen), the title fading under it. */
export const ACTION_OPEN_MS = 240;

/** Right-edge action slot — the back button's mirror (see BackHeaderProps.action). */
export const ActionSlot = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
`;

export const BackButton = styled.button`
  position: absolute;
  left: 0;
  /* Anchored to the title row (the top 32px), not the full box, so the fade tail
     (Root's padding-bottom) doesn't drop the arrow below the title. */
  top: 0;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  flex: 0 0 auto;
  border: none;
  border-radius: 99px;
  background: transparent;
  color: ${COLORS.text};
  cursor: pointer;
`;

/**
 * Centred drill-in title with the Figma gradient sweep (lilac → mauve → tan),
 * `bg-clip-text` over a heavy Black weight. `inline-block` sizes the gradient to
 * the text itself (so the full sweep shows on the glyphs); `max-width` keeps it
 * clear of the absolutely-positioned back button on narrow widths.
 *
 * `background-clip: text` only paints the gradient INSIDE the element's box, and
 * that box's height is the line box. At weight 900 the cap tops, descenders and
 * accented capitals (e.g. localized titles) overshoot a tight line box, so those
 * glyph parts get no gradient and fall back to the transparent fill — they read as
 * "cut off". The roomier line-height plus a little vertical padding grow the box to
 * contain the full glyph extents. The negative block margin keeps the row's outer
 * footprint unchanged so the pinned title doesn't shift.
 */
export const Title = styled(Text)<{ $withAction?: boolean }>`
  display: inline-block;
  /* With a right-edge action, keep the centred title clear of BOTH pinned edges
     (the action pill is wider than the back arrow). */
  max-width: ${(p) => (p.$withAction ? 'calc(100% - 224px)' : 'calc(100% - 88px)')};
  text-align: center;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.4;
  padding-block: 2px;
  margin-block: -2px;
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

/* A title that is a control (BackHeaderProps.onTitleClick): the title and the glyph after it, centred as the title is,
   kept clear of the pinned edges as it is; the title inside it is cut with an ellipsis rather than wrapped. */
export const TitleButton = styled.button<{ $withAction?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: ${(p) => (p.$withAction ? 'calc(100% - 224px)' : 'calc(100% - 88px)')};
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;

  & > ${Title} {
    min-width: 0;
    max-width: none;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & > svg {
    flex: none;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

/* The title over its subtitle (BackHeaderProps.subtitle), centred together, kept clear of the pinned edges as a title
   alone is; the title inside is cut with an ellipsis rather than wrapped. */
export const TitleStack = styled.div<{ $withAction?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
  max-width: ${(p) => (p.$withAction ? 'calc(100% - 224px)' : 'calc(100% - 88px)')};

  & > ${Title}, & > ${TitleButton} {
    max-width: 100%;
  }
`;

/**
 * Pinned drill-in title row.
 *
 *  - **Sticky.** It sticks flush under the layout top bar (`.pg-body-top`) so the
 *    title + back arrow stay put while the page scrolls. `top` matches PageShell's
 *    header heights per regime (the config flips the top bar to a fixed overlay at
 *    899px); `+ env(safe-area-inset-top)` keeps it clear of the notch, mirroring
 *    PageShell's own clearances.
 *  - **Above the scrim.** `z-index: 5` paints it over PageShell's header scrim
 *    (`z-index: 1`) while staying under the top bar (`z-index: 30`), so the scrim
 *    falls beneath the title instead of fading it out.
 *  - **Solid fill above (the `::before`).** PageShell's scrim — and on mobile the
 *    top bar's own gradient background — fade to TRANSPARENT exactly where this row
 *    pins, so without a backing, content scrolling up shows through the band
 *    between the top bar and the title. The `::before` fills that band (top bar
 *    bottom → title top) with the SOLID page colour. It's glued to the row's top
 *    edge (`bottom: 100%`) and as tall as the pin offset, so it always bridges the
 *    gap at any scroll position; it sits above the scrim (inside this row's
 *    `z-index: 5` context) but under the top bar (`z-index: 30`), which still paints
 *    its title/balance over it. This lives here, not in PageShell's scrim, because
 *    that scrim is shared by every page and non-drill pages have no title row — they
 *    must keep the plain gradient scrim. So: no BackHeader → gradient scrim; with a
 *    BackHeader → solid fill above + a fade below.
 *  - **Own fade backing (below).** The row carries the black→transparent
 *    `headerScrim` gradient so content fades out as it scrolls beneath the pinned
 *    title rather than butting up against it. `padding-bottom` is the fade tail; the
 *    negative `margin-bottom` keeps the gap to the next section close to the
 *    original now that the tail adds height.
 */
export const Root = styled.div<{ $stack?: boolean; $titleless?: boolean; $subtitled?: boolean; $actionOpen?: boolean }>`
  position: sticky;
  /* Pin under the layout top bar. On desktop the support-impersonation strip is already
     accounted for by the layout Root's top padding (--ows-imp-offset), which pushes the
     whole scroll container — and so this sticky element's scrollport — down by the strip
     height. Re-adding --ows-imp-offset to this scrollport-relative offset would
     double-count it: at the very top of the scroll the sticky clamp would drag the row
     (and its scrim) ~the strip height down OVER the resting content, and when pinned it
     would float that far below the top bar. So the desktop offset is just the header
     height. (Mobile is a different regime — see the FIXED_HEADER override below.)
     --ows-imp-safe-top (0 while the strip is up) drops the notch inset the strip already
     owns, to avoid double-counting it. */
  top: calc(${HEADER_DESKTOP_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)));
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding-bottom: 20px;
  margin-bottom: -14px;
  background: ${GRADIENTS.headerScrim};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    height: calc(${HEADER_DESKTOP_PX}px + env(safe-area-inset-top));
    background: ${COLORS.pageBg};
  }

  ${FIXED_HEADER} {
    /* Mobile keeps --ows-imp-offset: here the top bar is a position:fixed overlay against
       the viewport (it doesn't ride the padded Root flow) and the document itself is the
       scroller, so the strip height is NOT already baked into the scrollport - this offset
       has to clear both the fixed header and the strip. */
    top: calc(${HEADER_MOBILE_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + var(--ows-imp-offset, 0px));
    &::before {
      height: calc(${HEADER_MOBILE_PX}px + env(safe-area-inset-top));
    }
  }

  /* Stacked phone regime (opt-in, see BackHeaderProps.stackActionOnMobile): an
     action cluster too wide to share the row with the centred title drops onto
     its own right-aligned row beneath it instead of overlapping the title. The
     whole stacked geometry lives here in one block: the slot leaves the pinned
     right edge and becomes a full-width second row, the back arrow anchors to
     the title row alone (its bottom: 20px would otherwise stretch it across
     both rows), and the title only has the arrow to stay clear of.

     Two numbers keep the taller box dressed. The title's line grows to the
     arrow's 32px so the two share a centreline — on its natural ~25px line the
     arrow (fixed at 32px from the box top) rode ~3px low, and a wrapped title
     now centres the arrow on its FIRST line rather than floating between the
     lines. And the backing re-anchors to the box bottom: headerScrim's solid
     stop is a percentage OF THE BOX, so on the taller box it would leave the
     action row over the fade — content scrolling under the pinned header read
     straight through the outline buttons' transparent interiors. The stacked
     scrim stays opaque down to the 20px fade tail whatever the box height. */
  ${(p) =>
    p.$stack &&
    css`
      ${MOBILE} {
        flex-wrap: wrap;
        background: ${GRADIENTS.headerScrimStacked};

        ${BackButton} {
          bottom: auto;
          height: 32px;
        }

        ${Title} {
          max-width: calc(100% - 88px);
          line-height: 32px;
        }

        ${ActionSlot} {
          position: static;
          width: 100%;
          justify-content: flex-end;
          margin-top: 10px;
        }
      }
    `}

  /* No title (the page shows its own heading below): collapse the band to the back arrow's
     height. Drop the fade tail (padding-bottom) and its compensating negative margin so the row
     is just the 32px arrow, and re-anchor the arrow to fill that row now that the tail is gone
     (its base bottom:20px assumes the 20px tail). */
  /* What the action, opened across the row (BackHeaderProps.actionOpen), covers: faded out under it, back as it closes. */
  ${BackButton}, ${Title}, ${TitleButton}, ${TitleStack} {
    transition: opacity ${ACTION_OPEN_MS}ms ${ANIM.EASE};
  }

  ${(p) =>
    p.$actionOpen &&
    css`
      ${ActionSlot} {
        left: 0;
      }

      ${BackButton}, ${Title}, ${TitleButton}, ${TitleStack} {
        opacity: 0;
        pointer-events: none;
      }
    `}

  ${REDUCED_MOTION} {
    ${BackButton}, ${Title}, ${TitleButton}, ${TitleStack} {
      transition: none;
    }
  }

  /* With a subtitle the box is taller than the title's line: the back arrow and the action stay level with the title
     (its ~29px line from the box's top), not with the middle of the title and the subtitle. */
  ${(p) =>
    p.$subtitled &&
    css`
      ${BackButton}, ${ActionSlot} {
        bottom: auto;
        height: 29px;
      }
    `}

  ${(p) =>
    p.$titleless &&
    css`
      padding-bottom: 0;
      margin-bottom: 0;

      ${BackButton} {
        bottom: 0;
      }
    `}
`;
